import { Box, IconButton } from "@material-ui/core"
import { useMemo } from "react"
import type { ReactElement } from "react"

import { connect } from "react-redux"
import { useStateMap } from "../../../hooks"
import type { MapDetailDto } from "../../../models"
import type { AppThunkDispatch, Comparison, MapAction, SelectedComparison } from "../../../redux"
import { SubTitle1 } from "../../atoms"

import "./small-map-card.styles.scss"

export interface ISmallMapCard {
  mapId?: string
  thumbnailSrc?: string
  title?: string
  comparison?: SelectedComparison | Comparison
  label?: ReactElement
  iconButtonIcon?: ReactElement
  className?: string
  onIconClick?: () => void
}

export interface ISmallMapCardStateProps {
  dispatch: AppThunkDispatch<MapAction>
}

function SmallMapCardView(props: ISmallMapCard & ISmallMapCardStateProps): ReactElement {
  const { mapId, thumbnailSrc, title, comparison, label, iconButtonIcon, className, onIconClick, dispatch } = props

  const map: MapDetailDto | undefined = useStateMap(mapId, dispatch)

  const projectImages: string[] = useMemo<string[]>(() => {
    if (!comparison) return []
    const imageUrls: string[] = [
      comparison.firstAssetThumbnailUrl as string,
      comparison.secondAssetThumbnailUrl as string
    ]

    if (comparison.thirdAssetThumbnailUrl) {
      imageUrls.push(comparison.thirdAssetThumbnailUrl)
    }
    if (comparison.fourthAssetThumbnailUrl) {
      imageUrls.push(comparison.fourthAssetThumbnailUrl)
    }
    return imageUrls
  }, [comparison])

  function getGridClassName(): string {
    switch (projectImages.length) {
      case 2:
        return "grid-2"
      case 3:
        return "grid-3"
      case 4:
        return "grid-4"
      default:
        return ""
    }
  }

  function MapCard(): ReactElement {
    if (comparison) {
      return (
        <>
          <Box className="thumbnail-container">
            <Box className={`comparison-card-header-images ${getGridClassName()}`}>
              {projectImages.map((image: string, index: number) => (
                <Box
                  key={`${index}-${image}`}
                  className="comparison-img"
                  style={{
                    backgroundImage: `url('${image}')`
                  }}
                />
              ))}
            </Box>
          </Box>
          <Box className="map-description">
            {label}
            <SubTitle1>{comparison.title}</SubTitle1>
          </Box>
        </>
      )
    }
    if (map) {
      return (
        <>
          <Box className="thumbnail-container">
            <img className="thumbnail" src={map.thumbnailUrl} alt={map.title} />
          </Box>
          <Box className="map-description">
            {label}
            <SubTitle1>
              {map.pageNumber} {map.pageSection}
            </SubTitle1>
            <SubTitle1>{map.title}</SubTitle1>
          </Box>
        </>
      )
    }
    if (thumbnailSrc && title) {
      return (
        <>
          <Box className="thumbnail-container">
            <img className="thumbnail" src={thumbnailSrc} alt={title} />
          </Box>
          <Box className="map-description">
            {label}
            <SubTitle1>{title}</SubTitle1>
          </Box>
        </>
      )
    }
    return <></>
  }

  return (
    <Box className={`small-map-card ${className || ""}`}>
      <Box className="small-map-card-description">
        <MapCard />
      </Box>
      <Box className="icon-container">
        <IconButton onClick={onIconClick}>{iconButtonIcon}</IconButton>
      </Box>
    </Box>
  )
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MapAction>) {
  return { dispatch }
}

export const SmallMapCard = connect(mapDispatchToProps)(SmallMapCardView)
